import React, { type FC, useState, useEffect } from 'react';
import { Form } from 'antd';
import { isEmpty } from 'lodash';
import { format, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { Space } from '@g2u/ui-kit';

import { FullAvailabilityStatus, SlotAvailability, type JsonDataItem } from 'types';
import { AppointmentSelection, Content, Intro } from 'components';
import { Actions, ServiceRates } from './components';
import { postAppointment, getVerifyTechnicianAvailability } from 'queries';
import { BookingStore } from 'store';
import { Toast, isTimeExpired, parseErrors } from 'helpers';
import styles from './technician.module.scss';

export const BookingTechnicianPage: FC = observer(() => {
  const { customer, technician, location, slot, setAppointment, setStep, setIsAvailabilityOverrideEnabled } =
    BookingStore;
  const {
    isNewCustomer,
    setLocation,
    setTechnician,
    setSlot,
    job,
    setServiceType,
    lostJob,
    setLostJob,
    resetBookingProgress,
    setPreviousJob,
    setTechniciansList,
    previousJob,
    productList,
    productSelection,
    isAvailabilityOverrideEnabled,
  } = BookingStore;
  const navigate = useNavigate();

  const [isTechConfirmed, setIsTechConfirmed] = useState<boolean>(false);
  const [hasSlotsToday, setHasSlotsToday] = useState<boolean>(false);
  const [isRemoteAvailableToday, setIsRemoteAvailableToday] = useState<boolean>(false);
  const [slots, setSlots] = useState<JsonDataItem | null>();
  const { serviceSelection, serviceType } = BookingStore;
  const [form] = Form.useForm();
  const [changedFormState, setChangedFormState] = useState<JsonDataItem>();
  const isTimeSlotExpiring =
    !!slot &&
    isTimeExpired(
      `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot.time}`,
      location?.timezone || customer?.timezone,
      2,
    );
  const isButtonActive =
    !!slot &&
    (!isTimeSlotExpiring || isTechConfirmed) &&
    ((slot.availability === SlotAvailability.Available && !slot.isSurrounding) ||
      (slot.availability === SlotAvailability.Confirmation && isTechConfirmed) ||
      (slot.isSurrounding && isTechConfirmed) ||
      (Object.values(FullAvailabilityStatus).includes(slot.availability as unknown as FullAvailabilityStatus) &&
        isTechConfirmed));

  const handleFormChange = (changed: JsonDataItem) => {
    setChangedFormState(changed);
  };

  const handleFormFinish = () => {
    !isAvailabilityOverrideEnabled
      ? holdAppointment({
          technicianIds: [technician?.ID],
          appointmentStart:
            slot?.day && `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`,
          serviceSelectionId: serviceSelection?.ID,
          suburbId: customer?.address.suburb_ID || location.ID,
          isTechnicianConfirmed: 1, // 1 is requested by BE
          jobType: form.getFieldValue('jobType'),
        })
      : verifyAvailability({
          technicianId: technician?.ID,
          startDate: slot?.day
            ? `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`
            : '',
          serviceSelectionId: serviceSelection?.ID,
          serviceType: form.getFieldValue('jobType'),
        });
  };

  const { mutate: holdAppointment, isLoading: loadingAppointment } = useMutation(postAppointment, {
    onSuccess: (data) => {
      setAppointment(data);
      navigate('/booking/payment');
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: verifyAvailability, isLoading: isVerifying } = useMutation(getVerifyTechnicianAvailability, {
    onSuccess: () => {
      navigate('/booking/payment');
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  useEffect(() => {
    setStep(2);
    setIsAvailabilityOverrideEnabled(false);
  }, []);

  return (
    <Content.$ className={styles.technician} dataTestId="technician-page">
      <Helmet>
        <title>Select technician & time</title>
      </Helmet>
      <Form form={form} colon={false} layout="horizontal" onValuesChange={handleFormChange} onFinish={handleFormFinish}>
        <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
          <Intro.$ />
          <AppointmentSelection.$
            form={form}
            timezone={location?.timezone || customer?.timezone}
            isConfirmed={isTechConfirmed}
            changedFields={changedFormState}
            isAvailabilityOverrideEnabled={isAvailabilityOverrideEnabled}
            onAvailabilityOverrideChange={setIsAvailabilityOverrideEnabled}
            onConfirm={(status: boolean) => {
              setIsTechConfirmed(status);
            }}
            onTodaySlotsLoaded={(slots?: JsonDataItem | null) => {
              setHasSlotsToday(slots?.appointments[format(new Date(), 'dd-MM-yyyy')]?.length > 0);
              setSlots(slots);
            }}
            onRemoteSlotsChecked={setIsRemoteAvailableToday}
            {...{
              isNewCustomer,
              customer,
              location,
              setLocation,
              setTechnician,
              technician,
              setSlot,
              slot,
              job,
              serviceSelection,
              setServiceType,
              lostJob,
              setLostJob,
              resetBookingProgress,
              setPreviousJob,
              setTechniciansList,
              previousJob,
              productList,
              productSelection,
              serviceType,
            }}
          >
            <ServiceRates.$
              serviceType={serviceType}
              selectionId={serviceSelection?.ID}
              hasSlotsToday={hasSlotsToday}
              isRemoteAvailableToday={isRemoteAvailableToday}
              hasSlots={!isEmpty(slots?.appointments)}
            />
          </AppointmentSelection.$>
          <Actions.$
            isLoading={loadingAppointment || isVerifying}
            hasSlot={Boolean(slot)}
            active={isButtonActive}
            onBack={() => {
              navigate('/booking/location');
            }}
            onNext={() => {
              form.submit();
            }}
          />
        </Space.$>
      </Form>
    </Content.$>
  );
});
